import { useLocalization } from "@fluent/react";
import { Navigate } from "react-router-dom";
import User from "byzantine/src/User";
import { Button, Row } from "@narmi/design_system";
import {
  ContextForm,
  InternationalPhoneTextInput,
  useFormData,
  useNotificationContext,
  PhoneTextInput,
  useLoadingContext
} from "cerulean";
import type { InstitutionFeatures } from "byzantine/src/types";
import { useCurrentUser } from "../../../../contexts/CurrentUserContext";
import { useInstitutionSettings } from "../../../../contexts/InstitutionSettingsContext";
import { useSudoContext } from "../../../../SudoContext";
import { useUserFeatures } from "../../../../contexts/UserFeaturesContext";
import { useFingerPrintData } from "../../../../fingerprint";
import useIsNprEnabled from "../../../../../src/modules/nonPermanentResidents/hooks/useIsNprEnabled";
import { checkAuthSession } from "../../../../../src/utils";

interface PhoneNumberEditDialogBodyProps {
  closeDialog: () => void;
}

const PhoneNumberEditDialogBody = ({
  closeDialog
}: PhoneNumberEditDialogBodyProps) => {
  const { currentUser, setCurrentUser } = useCurrentUser();
  const { isNprEnabled } = useIsNprEnabled();
  const isNprEnabledForUser = isNprEnabled(currentUser || undefined);
  const { sendNotificationToParent } = useNotificationContext();
  const { formData, onChange } = useFormData({
    number: currentUser?.phone?.number || ""
  });
  const { l10n } = useLocalization();
  const features = useUserFeatures() as InstitutionFeatures;
  const { setIsLoading } = useLoadingContext();
  const { establishSudo } = useSudoContext();
  const { deviceId, deviceRequestId } = useFingerPrintData("phone_change");

  const { sudo_mode_required_for_phone_change: sudoRequired } =
    useInstitutionSettings();

  const validateField = (value: string) => {
    if (!value) {
      return l10n.getString("error-required", null, "Must be filled in.");
    }

    return null;
  };

  const onSubmit = async (callback: (arg?: unknown) => void) => {
    const updatedUser = await currentUser?.updatePhoneNumber(
      formData.number,
      deviceId,
      deviceRequestId
    );
    if (updatedUser) {
      // `cleanedUpdatedUser` is a `updatedUser` object with properties removed
      // if they are empty or undefined. We preserve `null` properties.
      //
      // We do this to prevent overriding unchanged values in `...currentUser`.
      const cleanedUpdatedUser = Object.fromEntries(
        Object.entries(updatedUser).filter(
          ([, v]) => ![undefined, '""', "[]", "{}"].includes(JSON.stringify(v))
        )
      );
      setCurrentUser(new User({ ...currentUser, ...cleanedUpdatedUser }));
    }
    callback();
    sendNotificationToParent({
      type: "dismissible_success",
      text: l10n.getString(
        "phone-updated-success-message",
        null,
        "Phone updated. If you intend to use this phone as a Two-Factor Authentication device, please add it [here](/settings/security)."
      )
    });
    closeDialog();
  };

  const onSubmitWrapper = async (callback: (arg?: unknown) => void) => {
    // different error handling depending on whether sudo mode is required
    if (sudoRequired) {
      setIsLoading(false);
      establishSudo("/profile", () => {
        setIsLoading(true);
        return onSubmit(callback).catch((error) => {
          const e = error as { number: string } | string;
          callback();
          sendNotificationToParent({
            type: "negative",
            text: typeof e === "string" ? e : e.number
          });
          checkAuthSession(l10n.getString("logout-message-blocked-and-locked"));
        });
      });
    } else {
      onSubmit(callback).catch((error) => {
        const e = error as { number: string } | string;
        callback(typeof e === "string" ? e : e.number);
        checkAuthSession(l10n.getString("logout-message-blocked-and-locked"));
      });
    }
  };

  if (!features?.phone_change) {
    return <Navigate to={"/profile"} replace />;
  }

  return (
    <ContextForm data={formData} onChange={onChange}>
      {isNprEnabledForUser ? (
        <ContextForm.Field field="number" validate={validateField}>
          <InternationalPhoneTextInput
            countryCodeLabel={l10n.getString(
              "label-international-wire-country-code"
            )}
            phoneNumberLabel={l10n.getString("label-phone-number")}
            onChange={(value: string) => onChange({ number: value })}
            value={formData.number}
          />
        </ContextForm.Field>
      ) : (
        <ContextForm.Field validate={validateField}>
          <PhoneTextInput
            label={l10n.getString("label-phone-number")}
            aria-label={l10n.getString("label-phone-number")}
            field="number"
          />
        </ContextForm.Field>
      )}
      <div className="margin--bottom--xl" />
      <Row alignItems="center" justifyContent="end">
        <Row.Item shrink>
          <Button
            type="button"
            onClick={closeDialog}
            kind="negative"
            label={l10n.getString("button-cancel")}
          />
        </Row.Item>
        <Row.Item shrink>
          <ContextForm.Action onSubmit={onSubmitWrapper}>
            <Button
              kind="primary"
              label={l10n.getString("button-save-changes")}
            />
          </ContextForm.Action>
        </Row.Item>
      </Row>
    </ContextForm>
  );
};

export default PhoneNumberEditDialogBody;
